<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="sizeClass"
      :style="{ '--pin-active-color': pinAccent }"
      :title="device.deviceName">
      <!-- The drawn server inside the status ring (P14): a healthy unit fills
           the ring green, a degraded one (a volume near full) keeps a shorter
           arc, an error keeps it full in the alarm red — a zero-length arc
           paints no arc at all, so the error says itself in the colour. -->
      <ControlStatusRing :value="pinRingValue" :accent="pinAccent">
        <ControlIcon name="server" :size="pinIconSize" :style="{ color: pinAccent }" />
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || kindIcon"
      :name="displayName"
      :is-on="isHealthy"
      active-color="green-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control / full mode ───────────────────────────────────────────── -->
  <!-- No head of its own: the sheet's chrome paints the circle, the name and
       the status (card t_488df70b); the body starts at the readouts. -->
  <template v-else>
    <div class="synology-sheet">
      <!-- NAS: the usage meters, each under its tile, then the readouts. -->
      <template v-if="kind === 'nas'">
        <div class="synology-grid">
          <div v-for="metric in usageMetrics" :key="metric.key" class="synology-cell">
            <ControlValueTile :icon="metric.icon" :label="metric.name" :value="metric.display" />
            <div class="synology-meter synology-meter--usage" :class="meterToneClass(metric.percent)">
              <div class="synology-meter-fill" :style="{ width: `${metric.percent}%` }"></div>
            </div>
          </div>
          <ControlValueTile
            v-if="readValue('temperature') !== undefined && readValue('temperature') !== null"
            icon="thermometer"
            :label="tt('Temperature', 'Temperatura')"
            :value="numberDisplay('temperature')" />
          <ControlValueTile
            v-if="readValue('uptime_seconds') !== undefined && readValue('uptime_seconds') !== null"
            icon="clock"
            :label="tt('Uptime', 'Čas delovanja')"
            :value="uptimeDisplay" />
        </div>

        <div v-if="boolValue('dsm_update_available')" class="synology-notice">
          <ControlIcon name="timer" size="1.0625rem" />
          {{ tt('A new DSM update is available', 'Na voljo je nova posodobitev DSM') }}
        </div>
      </template>

      <!-- Volume: the capacity row + meter, the tiles, then the state pills. -->
      <template v-else-if="kind === 'volume'">
        <div class="synology-row">
          <div class="synology-row-head">
            <span class="synology-row-label">{{ tt('Volume used', 'Zasedenost nosilca') }}</span>
            <span class="synology-row-value">{{ volumeCapacityLabel }}</span>
          </div>
          <div class="synology-meter synology-meter--volume" :class="meterToneClass(usedPercent)">
            <div class="synology-meter-fill" :style="{ width: `${usedPercent}%` }"></div>
          </div>
        </div>

        <div class="synology-grid">
          <ControlValueTile
            v-if="readValue('used_gb') !== undefined && readValue('used_gb') !== null"
            icon="server"
            :label="tt('Used', 'Porabljeno')"
            :value="numberDisplay('used_gb')"
            unit="GB" />
          <ControlValueTile
            v-if="readValue('total_gb') !== undefined && readValue('total_gb') !== null"
            icon="server"
            :label="tt('Total', 'Skupaj')"
            :value="numberDisplay('total_gb')"
            unit="GB" />
        </div>

        <div class="synology-pills">
          <span v-if="boolValue('volume_error')" class="synology-pill synology-pill--alarm">
            <ControlIcon name="bell" size="0.9375rem" />
            {{ tt('Volume error', 'Napaka nosilca') }}
          </span>
          <span v-if="boolValue('disk_full')" class="synology-pill synology-pill--warn">
            <ControlIcon name="timer" size="0.9375rem" />
            {{ tt('Almost full', 'Skoraj poln') }}
          </span>
        </div>
      </template>

      <!-- Disk: temperature + SMART, then the drive's own row. -->
      <template v-else>
        <div class="synology-grid">
          <ControlValueTile
            v-if="readValue('temperature') !== undefined && readValue('temperature') !== null"
            icon="thermometer"
            :label="tt('Temperature', 'Temperatura')"
            :value="numberDisplay('temperature')"
            unit="°C" />
          <ControlValueTile icon="activity" :label="tt('SMART', 'SMART')" :value="boolValue('smart_ok') ? 'OK' : '—'" />
        </div>

        <div
          v-if="readValue('model') || (readValue('size_gb') !== undefined && readValue('size_gb') !== null)"
          class="synology-drive-row">
          <ControlIcon name="server" size="1.0625rem" />
          <span class="synology-drive-text">
            {{ readValue('model') || ''
            }}{{
              readValue('size_gb') !== undefined && readValue('size_gb') !== null ? ` · ${readValue('size_gb')} GB` : ''
            }}
          </span>
        </div>
      </template>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';

import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import ControlValueTile from '../device-sheet/ControlValueTile.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import { tt } from '../../utils/consoleText';
import type { Device, DeviceValueWithReferences } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── Live values ──────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

function valueDef(key: string): DeviceValueWithReferences | undefined {
  return (props.device.deviceValues ?? []).find((v) => v.valueKey === key);
}

function readValue(key: string): unknown {
  if (key in currentValues.value) return currentValues.value[key];
  return valueDef(key)?.currentValue ?? undefined;
}

function toNumber(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function boolValue(key: string): boolean {
  const raw = readValue(key);
  return raw === true || raw === 1 || raw === 'true' || raw === 'on';
}

const kind = computed<'nas' | 'volume' | 'disk'>(() => {
  const type = String(props.device.deviceType || props.device.controlType || '').toLowerCase();
  if (type.includes('volume')) return 'volume';
  if (type.includes('disk')) return 'disk';
  return 'nas';
});

const kindIcon = computed(() => {
  if (kind.value === 'volume') return 'storage';
  if (kind.value === 'disk') return 'sd_storage';
  return 'database';
});

// Status per device kind.
const online = computed(() => boolValue('online'));
const hasVolumeError = computed(() => boolValue('volume_error'));
const hasDiskError = computed(() => boolValue('disk_error'));
const almostFull = computed(() => boolValue('disk_full'));

const errorState = computed(() =>
  kind.value === 'nas'
    ? !online.value
    : kind.value === 'volume'
      ? hasVolumeError.value
      : kind.value === 'disk'
        ? hasDiskError.value
        : false,
);
const isHealthy = computed(() => !errorState.value);

function numberDisplay(key: string): string {
  const n = toNumber(readValue(key));
  if (n === null) return '—';
  return `${Math.round(n * 10) / 10}`;
}

// NAS metrics
interface UsageMetric {
  key: string;
  name: string;
  /** A ControlIcon name — never a Material glyph. */
  icon: string;
  display: string;
  percent: number;
}

function percentOf(raw: unknown): number {
  const n = toNumber(raw);
  if (n === null) return 0;
  return Math.max(0, Math.min(100, n));
}

function buildMetric(key: string, fallbackName: string, icon: string): UsageMetric {
  const def = valueDef(key);
  const n = toNumber(readValue(key));
  const name = def?.name || fallbackName;
  return {
    key,
    name,
    icon,
    display: n === null ? '—' : `${Math.round(n * 10) / 10}${def?.unit ? ` ${def.unit}` : ''}`,
    percent: percentOf(readValue(key)),
  };
}

const usageMetrics = computed<UsageMetric[]>(() => [
  buildMetric('cpu_load', 'CPU', 'activity'),
  buildMetric('memory_used', tt('Memory', 'Pomnilnik'), 'server'),
]);

const usedPercent = computed(() => percentOf(readValue('used_percent')));
const volumeCapacityLabel = computed(() => {
  const used = toNumber(readValue('used_gb'));
  const total = toNumber(readValue('total_gb'));
  if (used === null && total === null) return `${Math.round(usedPercent.value)}%`;
  const usedPart = used !== null ? `${used} GB` : '—';
  const totalPart = total !== null ? `${total} GB` : '';
  return `${Math.round(usedPercent.value)}% · ${usedPart}${totalPart ? ` ${tt('of', 'od')} ${totalPart}` : ''}`;
});

const uptimeSeconds = computed<number | null>(() => toNumber(readValue('uptime_seconds')));
const uptimeDisplay = computed(() => formatUptime(uptimeSeconds.value));

const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);

const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** The pin's tone (P14): the storage family's healthy green, the alarm red on error. */
const pinAccent = computed(() => (errorState.value ? 'var(--device-color-alarm)' : 'var(--device-color-switch)'));

/**
 * The pin's ring (P14): 100 while the unit is healthy, a shorter arc for a unit
 * that runs but is degraded (a volume near full), and 100 in the alarm red when
 * it errors — the ring paints no arc at all for a 0, so an error has to keep the
 * full ring and say itself in the colour.
 */
const pinRingValue = computed(() => {
  if (errorState.value) return 100;
  return almostFull.value ? 50 : 100;
});

/** The drawn server's size inside the pin (P14), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

/**
 * The meter's tone — the old bar's own thresholds (amber from 80 %, the alarm
 * tone from 90 %), now driven by the sheet's tokens instead of utilities.
 */
function meterToneClass(percent: number): string {
  if (percent >= 90) return 'synology-meter--alarm';
  if (percent >= 80) return 'synology-meter--warn';
  return 'synology-meter--ok';
}

function formatUptime(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
</script>

<style scoped>
/* Control mode: the readouts in the sheet language. The host chrome owns the
   head and the panel; this block is the tile grid, the usage meters and the
   state pills. No amber token exists in the sheet, so the warning tone is a
   literal here — the alarm and healthy tones ride `--device-color-*`. */
.synology-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(0.875rem * var(--ui-space-factor));
}

.synology-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

@media (min-width: 640px) {
  .synology-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/* A metric cell: its tile, the meter under it (the old bars, restyled). */
.synology-cell {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}

.synology-meter {
  height: 0.375rem;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.22);
  overflow: hidden;
}

.dark .synology-meter {
  background: rgba(255, 255, 255, 0.08);
}

.synology-meter--volume {
  height: 0.75rem;
}

.synology-meter-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, color-mix(in srgb, var(--meter-tone) 55%, transparent), var(--meter-tone));
  transition: width 0.7s ease-out;
}

.synology-meter--ok {
  --meter-tone: var(--device-color-switch);
}

.synology-meter--warn {
  --meter-tone: rgb(245, 158, 11);
}

.synology-meter--alarm {
  --meter-tone: var(--device-color-alarm);
}

/* The volume's capacity row — the sheet's label/value row above its meter. */
.synology-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.synology-row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: calc(0.75rem * var(--ui-space-factor));
}

.synology-row-label {
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .synology-row-label {
  color: rgb(var(--gray-400));
}

.synology-row-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(var(--gray-900));
}

.dark .synology-row-value {
  color: #fff;
}

/* The state pills (volume error / almost full) in the chip recipe. */
.synology-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.synology-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.375em;
  padding: calc(0.25rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor));
  border: 1px solid;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.synology-pill--alarm {
  color: var(--device-color-alarm);
  border-color: color-mix(in srgb, var(--device-color-alarm) 45%, transparent);
  background: color-mix(in srgb, var(--device-color-alarm) 14%, transparent);
}

.synology-pill--warn {
  color: rgb(180, 83, 9);
  border-color: rgba(245, 158, 11, 0.4);
  background: rgba(245, 158, 11, 0.14);
}

.dark .synology-pill--warn {
  color: rgb(252, 211, 77);
}

/* The DSM update notice — the old amber row, restyled to the sheet. */
.synology-notice {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: calc(0.625rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor));
  border: 1px solid rgba(245, 158, 11, 0.4);
  border-radius: calc(0.75rem * var(--ui-radius-factor));
  background: rgba(245, 158, 11, 0.12);
  font-size: 0.875rem;
  color: rgb(180, 83, 9);
}

.dark .synology-notice {
  color: rgb(252, 211, 77);
}

/* The drive's model · size row — the old card row, in the tile recipe. */
.synology-drive-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: calc(0.625rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: calc(0.75rem * var(--ui-radius-factor));
  background: rgba(148, 163, 184, 0.1);
  font-size: 0.875rem;
  color: rgb(var(--gray-600));
}

.dark .synology-drive-row {
  background: rgba(255, 255, 255, 0.04);
  color: rgb(var(--gray-300));
}

.synology-drive-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
